/**
 * React Query hooks for document metadata and taxonomy.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    documentService,
    type DocumentMetadata,
    type TaxonomyDomain,
    type OverrideClassificationPayload,
    type CorrectFactResponse,
} from '../../services/documentService';

export const metadataKeys = {
    all: ['document-metadata'] as const,
    detail: (docId: string) => [...metadataKeys.all, docId] as const,
};

export const taxonomyKeys = {
    all: ['taxonomy'] as const,
    domains: () => [...taxonomyKeys.all, 'domains'] as const,
};

/**
 * Hook to fetch full document metadata (extracted facts + classification).
 * Only fetches when docId is provided (i.e., when preview is open).
 */
export function useDocumentMetadata(docId: string | null) {
    return useQuery<DocumentMetadata>({
        queryKey: metadataKeys.detail(docId!),
        queryFn: () => documentService.getDocumentMetadata(docId!),
        enabled: !!docId,
    });
}

/**
 * classifier-v3 (#1066): fetch the two-axis (domain → role) taxonomy that
 * drives the classification override selectors. The legacy category/subcategory/
 * type tree hook (``useTaxonomyTree``) + type-override hook were retired in #1065.
 * Mounted only when the ``documents.classifier_v3`` gate is on (``enabled``).
 * Cached since the taxonomy is mostly static.
 */
export function useTaxonomyDomains(enabled = true) {
    return useQuery<TaxonomyDomain[]>({
        queryKey: taxonomyKeys.domains(),
        queryFn: () => documentService.getTaxonomyDomains(),
        staleTime: 5 * 60 * 1000,
        enabled,
    });
}

/**
 * classifier-v3 (#1066): override a document's native (domain, role) cell.
 * The backend validates the cell against taxonomy-v2, maps it to the legacy
 * triple, and reprocesses. Invalidates the metadata cache on success so the
 * preview round-trips the new classification. This is the sole classification-
 * override hook since the legacy type-override was retired in #1065.
 */
export function useOverrideClassification() {
    const queryClient = useQueryClient();
    return useMutation<
        { doc_id: string; job_id: string; domain: string; role: string },
        Error,
        { docId: string; payload: OverrideClassificationPayload }
    >({
        mutationFn: ({ docId, payload }) => documentService.overrideClassification(docId, payload),
        onSuccess: (_data, { docId }) => {
            queryClient.invalidateQueries({ queryKey: metadataKeys.detail(docId) });
        },
    });
}

/**
 * Hook to update fact overrides for a document.
 * Invalidates metadata cache on success so the panel reflects the new values.
 *
 * #386 subtask 05: optional ``pickedEntityId`` carries an autocomplete
 * pick — operator selected an existing canonical entity for this field.
 * Passes through to the backend as ``picked_entity_id_by_field`` so the
 * worker recalc bypasses L1-L4 dedup.
 */
export function useUpdateFactOverrides() {
    const queryClient = useQueryClient();
    return useMutation<
        { doc_id: string; extracted_facts: Record<string, unknown> },
        Error,
        { docId: string; fieldId: string; value: string; pickedEntityId?: string }
    >({
        mutationFn: ({ docId, fieldId, value, pickedEntityId }) =>
            documentService.updateFactOverrides(
                docId,
                { [fieldId]: value },
                pickedEntityId ? { pickedEntityIdByField: { [fieldId]: pickedEntityId } } : undefined,
            ),
        onSuccess: (_data, { docId }) => {
            queryClient.invalidateQueries({ queryKey: metadataKeys.detail(docId) });
        },
    });
}

/**
 * Hook for L0 fact correction (issue #620 subtask 06 / #706). Use when
 * the operator clicks an inline validation badge; the correction lands
 * on ``POST /facts/correct`` which emits ``document.fact_corrected``
 * and triggers a targeted twin re-link.
 */
export function useCorrectFact() {
    const queryClient = useQueryClient();
    return useMutation<
        CorrectFactResponse,
        Error,
        { docId: string; fieldId: string; value: unknown; validatorId?: string }
    >({
        mutationFn: ({ docId, fieldId, value, validatorId }) =>
            documentService.correctFact(docId, {
                field_id: fieldId,
                value,
                validator_id: validatorId,
            }),
        onSuccess: (_data, { docId }) => {
            queryClient.invalidateQueries({ queryKey: metadataKeys.detail(docId) });
        },
    });
}
